import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { BarChart3, PieChart, LineChart, VoteIcon } from "lucide-react";

const UnderstandingCards = () => {
  return (
    <section className="py-16 px-4 bg-white dark:bg-gray-900">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">
            Understanding NationCard™
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Learn how our core features work together to amplify citizen voices
            and drive change
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Polls Card */}
          <Card className="overflow-hidden border-2 border-nation-teal/20">
            <CardHeader className="bg-nation-teal/5 pb-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-nation-teal">
                  Poll Cards
                </h3>
                <VoteIcon className="h-6 w-6 text-nation-teal" />
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="mb-4">
                Polls are the backbone of NationCard™. They gather verified
                citizen opinions on important issues affecting the Cayman
                Islands.
              </p>

              <h4 className="font-semibold mb-2">How Polls Work:</h4>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start">
                  <span className="text-nation-teal mr-2">•</span>
                  <span>
                    Verified pollsters are randomly selected based on
                    demographic representation
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-nation-teal mr-2">•</span>
                  <span>
                    Each poll focuses on a specific issue or proposed solution
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-nation-teal mr-2">•</span>
                  <span>Results are analyzed and published in real-time</span>
                </li>
                <li className="flex items-start">
                  <span className="text-nation-teal mr-2">•</span>
                  <span>
                    Detailed demographic breakdowns reveal consensus across
                    different groups
                  </span>
                </li>
              </ul>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Poll Results:</span>
                  <div className="flex items-center">
                    <PieChart className="h-4 w-4 text-nation-purple mr-1" />
                    <LineChart className="h-4 w-4 text-nation-teal" />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Results feed directly into Insight Cards to provide
                  contextualized data on public opinion.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Insight Cards */}
          <Card className="overflow-hidden border-2 border-nation-purple/20">
            <CardHeader className="bg-nation-purple/5 pb-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-nation-purple">
                  Insight Cards
                </h3>
                <BarChart3 className="h-6 w-6 text-nation-purple" />
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="mb-4">
                Insight Cards transform poll data into digestible information
                about key issues. They show what Caymanians really think about
                important topics.
              </p>

              <h4 className="font-semibold mb-2">
                What Makes Insight Cards Powerful:
              </h4>
              <ul className="space-y-2 mb-4">
                <li className="flex items-start">
                  <span className="text-nation-purple mr-2">•</span>
                  <span>
                    Data-driven summaries of public sentiment on specific issues
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-nation-purple mr-2">•</span>
                  <span>Agreement percentages show the level of consensus</span>
                </li>
                <li className="flex items-start">
                  <span className="text-nation-purple mr-2">•</span>
                  <span>
                    Cards link directly to related polls for deeper analysis
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-nation-purple mr-2">•</span>
                  <span>
                    Trends can be tracked over time to measure changing opinions
                  </span>
                </li>
              </ul>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">
                    From Data to Action:
                  </span>
                  <BarChart3 className="h-4 w-4 text-nation-purple" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Insight Cards lay the foundation for Solution and Action
                  Cards, moving from understanding problems to solving them.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* The Connection */}
        <div className="bg-gradient-to-r from-nation-purple/10 to-nation-teal/10 dark:from-nation-purple/20 dark:to-nation-teal/20 p-6 rounded-lg text-center">
          <h3 className="text-lg font-semibold mb-2">How They Work Together</h3>
          <p>
            Polls collect structured data, Insight Cards interpret that data,
            and our upcoming Solution & Action Cards will turn those insights
            into real-world impact. This complete cycle ensures citizen voices
            directly shape Cayman's future.
          </p>
        </div>
      </div>
    </section>
  );
};

export default UnderstandingCards;
